import { Autocomplete, FormGroup, Popper, PopperProps, TextField } from '@mui/material'
import { C1FormLabel } from 'components/core/RHKFormComponents/C1FormLabel'
import React, { ReactElement } from 'react'
import { useIntl } from 'react-intl'

const AutocompletePopper = (props: PopperProps & { 'aria-label'?: string }): ReactElement => (
    <Popper {...props} role="region" />
)

export type AutocompleteStaticOptionType = {
    category: string
    label: string
    value: string
}

const fuzzyMatch = (needle: string, haystack: string): boolean => {
    if (!needle) {
        return true
    }

    let i = -1
    for (const n of needle) {
        let found = false
        while (i < haystack.length - 1) {
            i++
            if (n === haystack[i]) {
                found = true
                break
            }
        }
        if (!found) {
            return false
        }
    }
    return true
}

const filterOptions = (
    validOptions: AutocompleteStaticOptionType[],
    { inputValue }: { inputValue: string },
): AutocompleteStaticOptionType[] => {
    return validOptions.filter((option) => fuzzyMatch(inputValue.toLowerCase(), option.label.toLowerCase()))
}

type AutocompleteStaticOptionsProps = {
    options: AutocompleteStaticOptionType[],
    handleChange: (event: React.SyntheticEvent<Element, Event>, newValue: AutocompleteStaticOptionType | null) => void
    label?: string
    value: AutocompleteStaticOptionType | null
}

export const AutocompleteStaticOptions = ({
    options,
    handleChange,
    label,
    value,
}: AutocompleteStaticOptionsProps): React.ReactElement => {
    const intl = useIntl()
    return <Autocomplete<AutocompleteStaticOptionType>
        value={value}
        onChange={handleChange}
        options={options}
        filterOptions={filterOptions}
        getOptionLabel={(option): string => option.label}
        renderInput={(params): ReactElement => <FormGroup>
            <C1FormLabel label={label} htmlFor="autocomplete" />
            <TextField
                name="autocomplete"
                {...params}
                fullWidth
                size="small"
                InputProps={{
                    ...params.InputProps,
                    type: 'autocomplete',
                    endAdornment: params.InputProps.endAdornment,
                }}
            />
        </FormGroup>}
        groupBy={(option): string => option.category}
        PopperComponent={(props): ReactElement => (
            <AutocompletePopper
                {...props}
                aria-label={intl.formatMessage({ id: 'autocomplete_search_results', defaultMessage: 'Search results' })}
            />
        )}
    />
}
